/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import {Button, ButtonGroup, Select, useNumberInput} from '@chakra-ui/react'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {BinIcon, MinusIcon, PlusIcon} from '../icons'

const QuantityPicker = ({
    btnWidth,
    containerProps,
    height = 11,
    removable,
    minQuantity = removable ? 0 : 1,
    maxQuantity = 5,
    quantity,
    quantityStep = 1,
    onChange,
    onFocus,
    onRemove
}) => {
    const {formatMessage} = useIntl()
    const {getInputProps, getIncrementButtonProps, getDecrementButtonProps, value, valueAsNumber} =
        useNumberInput({
            defaultValue: quantity || 1,
            value: quantity,
            min: removable ? 0 : minQuantity,
            step: quantityStep,
            focusInputOnChange: false,
            onChange: (strValue, intValue) => {
                if (intValue < minQuantity || intValue === 0) {
                    if (onRemove) {
                        onRemove()
                        return false
                    }
                    if (onChange) {
                        return onChange(strValue, intValue)
                    }
                    return false
                } else if (intValue > maxQuantity && intValue < quantity) {
                    return onChange(strValue, maxQuantity)
                } else {
                    return onChange?.(strValue, intValue)
                }
            },
            onFocus
        })

    const showRemove = removable && valueAsNumber <= Math.max(1, minQuantity)

    const input = getInputProps({
        maxWidth: '44px',
        textAlign: 'center',
        // safari bug https://bugs.webkit.org/show_bug.cgi?id=40216
        // and SO answer https://stackoverflow.com/a/76858497
        sx: {
            textAlignLast: 'center'
        },
        'aria-label': formatMessage({
            defaultMessage: 'Quantity',
            id: 'product_view.label.quantity'
        })
    })
    const inc = getIncrementButtonProps({
        'aria-label': formatMessage({
            defaultMessage: 'Increment Quantity',
            id: 'product_view.label.assistive_msg.quantity_increment'
        })
    })
    const dec = getDecrementButtonProps({
        'aria-label': !showRemove
            ? formatMessage({
                  defaultMessage: 'Decrement Quantity',
                  id: 'product_view.label.assistive_msg.quantity_decrement'
              })
            : formatMessage({
                  defaultMessage: 'Remove',
                  id: 'global.remove'
              })
    })

    // Accessibility improvements:
    // 1. Allow keyboard focus on the buttons - Chakra overrides values passed to get*ButtonProps()
    inc.tabIndex = ''
    dec.tabIndex = ''
    // 2. Allow Space or Enter key to trigger buttons
    // Hitting space/enter triggers a "click" event, but the component listens for "mousedown".
    // We can't reuse the buttons' onMouseDown handler, so instead we hijack the input's onKeyDown.
    // @ref https://github.com/chakra-ui/chakra-ui/blob/%40chakra-ui/react%402.7.0/packages/components/number-input/src/use-number-input.ts#L333-L334
    inc.onKeyDown = (evt) => {
        if (evt.key === ' ' || evt.key === 'Enter') {
            evt.key = 'ArrowUp'
            input.onKeyDown(evt)
        }
    }
    dec.onKeyDown = (evt) => {
        if (evt.key === ' ' || evt.key === 'Enter') {
            evt.key = 'ArrowDown'
            input.onKeyDown(evt)
        }
    }
    // 3. remove role and invalid aria attributes from the input as we use select with options
    delete input.role
    delete input['aria-valuemin']
    delete input['aria-valuemax']
    delete input['aria-valuenow']
    delete input['aria-valuetext']

    // Disable decrement button if quantity is at minimum
    if (valueAsNumber <= minQuantity && !removable) {
        dec.disabled = true
        dec.isDisabled = true
        dec['aria-disabled'] = true
    }

    // Never disable increment button
    delete inc.disabled
    delete inc.isDisabled
    delete inc['aria-disabled']

    return (
        <ButtonGroup
            isAttached
            variant="cartSecondary"
            border="1px"
            borderColor="#EFEFEF"
            borderRadius="4px"
            height={height}
            width="100%"
            {...containerProps}
        >
            <Button
                data-testid="quantity-decrement"
                borderRadius={0}
                height={height}
                width={btnWidth}
                minWidth={btnWidth}
                {...dec}
            >
                {!showRemove ? <MinusIcon boxSize={4} /> : <BinIcon boxSize={4} />}
            </Button>
            <Select
                size="sm"
                variant="unstyled"
                icon={<></>}
                maxWidth="none"
                maxW="none"
                minWidth="fit-content"
                height={height}
                {...input}
            >
                {minQuantity >= 1 && removable ? <option value="0">0</option> : null}
                {Array.from({length: maxQuantity - minQuantity + 1}, (_, i) => i + minQuantity).map(
                    (i) => (
                        <option key={i} value={i}>
                            {i}
                        </option>
                    )
                )}
                {valueAsNumber > maxQuantity && (
                    <option disabled value={value}>
                        {value}
                    </option>
                )}
            </Select>
            <Button
                data-testid="quantity-increment"
                borderRadius={0}
                height={height}
                width={btnWidth}
                minWidth={btnWidth}
                {...inc}
            >
                <PlusIcon boxSize={4} />
            </Button>
        </ButtonGroup>
    )
}

QuantityPicker.propTypes = {
    btnWidth: PropTypes.any,
    containerProps: PropTypes.object,
    height: PropTypes.any,
    removable: PropTypes.bool,
    minQuantity: PropTypes.number,
    maxQuantity: PropTypes.number,
    quantity: PropTypes.number,
    quantityStep: PropTypes.number,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onRemove: PropTypes.func
}

export default QuantityPicker
